import {
  OpenAPIConfig,
  AppClient as Authorization,
} from '@fountain/wx-api-clients/generated/client_instances/authorization';
import { AppClient as Security } from '@fountain/wx-api-clients/generated/client_instances/security';
import { AppClient as Organizations } from '@fountain/wx-api-clients/generated/client_instances/organizations';

type BaseAppClientInstance = { request: { config: OpenAPIConfig } };

// Why does this exist?
//
// There is a need to handle multiple session tokens and specify which to use per route.
// The primary method we use in this codebase results in tokens being managed globally.
//
// This class uses `generated/client_instances` routes, which take a base client
// where we can use to specify a different token.
//
// ---
//
// There is a problem with this approach:
//
// The generated routes are just way to big (no treeshaking). Loading every service
// leads to an increase of 120kb gzipped to our bundle size. Since this is required
// to load data, it could be loaded on every page.
//
// This is not ideal. To mitigate this, I am choosing to manually specify the services
// this client uses. This will temporarily fix the issue, but will need to be revisited

class BaseAppClient {
  constructor() {}

  #instances: BaseAppClientInstance[] = [];

  protected debugHeader = { 'X-Authz-Development': 'true' };

  protected config: Partial<OpenAPIConfig> = {
    TOKEN: undefined,
    HEADERS: undefined,
  };

  setBearerToken(token: string) {
    this.config.TOKEN = token;

    this.#instances.forEach(instance => {
      instance.request.config.TOKEN = token;
    });
  }

  removeBearerToken() {
    this.config.TOKEN = undefined;

    this.#instances.forEach(instance => {
      instance.request.config.TOKEN = undefined;
    });
  }

  setDebugHeader() {
    this.config.HEADERS = this.debugHeader;

    this.#instances.forEach(instance => {
      instance.request.config.HEADERS = this.debugHeader;
    });
  }

  removeDebugHeader() {
    this.config.HEADERS = undefined;

    this.#instances.forEach(instance => {
      instance.request.config.HEADERS = undefined;
    });
  }

  public security = this.registerClient({
    client: Security,
    config: {
      BASE: import.meta.env.VITE_SECURITY_SERVICE_BASE_URL,
    },
  });

  public authorization = this.registerClient({
    client: Authorization,
    config: {
      BASE: import.meta.env.VITE_AUTHORIZATION_SERVICE_BASE_URL,
    },
  });

  public organizations = this.registerClient({
    client: Organizations,
    config: {
      BASE: import.meta.env.VITE_ORGANIZATIONS_SERVICE_BASE_URL,
    },
  });

  registerClient<T>({
    client: Client,
    config,
  }: {
    client: { new (config: Partial<OpenAPIConfig>): T & BaseAppClientInstance };
    config: Partial<OpenAPIConfig>;
  }) {
    const instance = new Client({
      ...this.config,
      ...config,
    });

    this.#instances.push(instance);

    return instance;
  }
}

// Lazily instantiate the BaseAppClient on first access. We return a Proxy so
// existing consumers can keep importing `enterpriseClient` without any
// changes, but we avoid constructing the real client at module initialization
// time (which can trigger early reads of build-time tokens in UMD packages).
let _lazyEnterpriseClient: BaseAppClient | null = null;
function getEnterpriseClient(): BaseAppClient {
  if (!_lazyEnterpriseClient) _lazyEnterpriseClient = new BaseAppClient();
  return _lazyEnterpriseClient;
}

// Backwards-compatible exported `enterpriseClient` that behaves like the
// singleton but doesn't trigger construction at module-eval time. This Proxy
// delegates all property accesses and method calls to the value returned by
// `getEnterpriseClient()` at access time.
export const enterpriseClient: BaseAppClient = new Proxy({} as BaseAppClient, {
  get(_, prop: PropertyKey) {
    const target = getEnterpriseClient();
    // Access via a safe unknown-based indexer to avoid `any` casts
    const indexed = target as unknown as Record<PropertyKey, unknown>;
    const value = indexed[prop];
    // If the property is a function, return it bound to the real instance so
    // `this` inside class methods points to the actual BaseAppClient (this
    // is necessary for methods that access private fields like `#instances`).
    if (typeof value === 'function') {
      // Bind with a generic callable signature to keep typing safe for unknown
      // function shapes while avoiding the banned `Function` type.
      const fn = value as (...args: unknown[]) => unknown;
      return fn.bind(target) as unknown;
    }

    return value as unknown;
  },
  set(_, prop: PropertyKey, value: unknown) {
    const target = getEnterpriseClient();
    const indexed = target as unknown as Record<PropertyKey, unknown>;
    indexed[prop] = value as unknown;
    return true;
  },
});
